import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

import { Loading } from "../../Common/OtherElements/Loading";
import MultipleFileUpload from "../../Common/FormElements/MultipleFileUpload";
import { handleErrors } from "../../../utils/errorHandler";
import Swal from "sweetalert2";
import { confirmDelete } from "../../Common/OtherElements/confirmDeleteClone";

import {
  createproductGalleryService,
  fetchProductsVariantByProductId,
  deleteproductGalleryService,
  updateGalleryOrderService,
} from "../../../services/productGalleryService";
import allImages from "../../../assets/images-import";

const ProductGalleryModal = ({
  show,
  onHide,
  productId,
  productName,
  onReset,
}) => {
  const [formValues, setFormValues] = useState({
    ProductId: productId,
    ColorId: 0,
    GalleryType: "Image",
    GalleryFiles: [],
  });

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [reorderLoading, setReorderLoading] = useState(false);
  const [manageProductsGallery, setManageProductsGallery] = useState([]);

  // Handle file changes from MultipleFileUpload component
  const handleFilesChange = (files) => {
    setSelectedFiles(files);
    setFormValues((prev) => ({
      ...prev,
      GalleryFiles: files,
    }));
  };

  const handleSubmit = async () => {
    if (!selectedFiles || selectedFiles.length === 0) {
      toast.error("Please select at least one image to upload!");
      return;
    }

    try {
      setUploadLoading(true);

      // Create FormData object to send files
      const formData = new FormData();
      formData.append("ProductId", formValues.ProductId);
      formData.append("ColorId", formValues.ColorId);
      formData.append("GalleryType", formValues.GalleryType);

      // Append all selected files
      selectedFiles.forEach((file, index) => {
        formData.append(`GalleryFiles`, file);
      });

      const response = await createproductGalleryService(formData);

      if (response.isSuccess) {
        Swal.fire("Success!", "Images uploaded successfully!", "success");

        // Reset form and clear selected files
        setSelectedFiles([]);
        setFormValues({
          ProductId: productId,
          ColorId: 0,
          GalleryType: "Image",
          GalleryFiles: [],
        });

        // Refresh the gallery images
        fetchGalleryImages();

        // Call onReset if provided
        if (onReset) {
          onReset();
        }
      }
    } catch (error) {
      handleErrors(error);
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDelete = async (productGalleryId) => {
    const confirmed = await confirmDelete("product gallery");
    if (confirmed) {
      try {
        await deleteproductGalleryService(productGalleryId);
        setManageProductsGallery((prev) =>
          prev.filter((item) => item.id !== productGalleryId)
        );
        Swal.fire(
          "Deleted!",
          "Product Gallery deleted successfully!",
          "success"
        );
      } catch (error) {
        handleErrors(error);
      }
    }
  };

  // React Beautiful DND handler
  const handleDragEnd = async (result) => {
    // If dropped outside the list or no destination
    if (!result.destination) {
      return;
    }

    // If dropped in the same position
    if (result.destination.index === result.source.index) {
      return;
    }
    // Create new array with reordered items
    const newGallery = Array.from(manageProductsGallery);
    const [reorderedItem] = newGallery.splice(result.source.index, 1);
    newGallery.splice(result.destination.index, 0, reorderedItem);

    // Update local state immediately for better UX
    setManageProductsGallery(newGallery);
  };

  const handleUpdateDisplayOrder = async () => {
    try {
      setReorderLoading(true);
      // Prepare data for API call
      const updateOrderData = manageProductsGallery.map((item, index) => ({
        id: item.id,
        displayOrder: index + 1,
      }));

      // Call API to update order
      const response = await updateGalleryOrderService(updateOrderData);

      if (response.isSuccess) {
        toast.success("Gallery order updated successfully!");
      } else {
        throw new Error("Failed to update gallery order");
      }
    } catch (error) {
      handleErrors(error);
      // Revert local changes on error
      fetchGalleryImages();
    } finally {
      setReorderLoading(false);
    }
  };

  const fetchGalleryImages = async () => {
    try {
      setLoading(true);
      const response = await fetchProductsVariantByProductId(productId);
      if (response.isSuccess) {
        // Sort by displayOrder if available, otherwise maintain original order
        const sortedGallery = response.result.sort((a, b) => {
          if (a.displayOrder && b.displayOrder) {
            return a.displayOrder - b.displayOrder;
          }
          return 0;
        });
        setManageProductsGallery(sortedGallery);
      }
    } catch (error) {
      handleErrors(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (productId) {
      fetchGalleryImages();
    }
  }, [productId]);

  // Reset form values when productId changes
  useEffect(() => {
    setFormValues({
      ProductId: productId,
      ColorId: 0,
      GalleryType: "Image",
      GalleryFiles: [],
    });
    setSelectedFiles([]);
  }, [productId]);

  return (
    <Modal show={show} onHide={onHide} centered fullscreen>
      <Modal.Header closeButton>
        <Modal.Title>
          Product Gallery - <b>{`(${productName})`}</b>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "unset" }}>
        <Form>
          <div className="row">
            <div className="col-lg-8">
              {loading ? (
                <Loading />
              ) : (
                <>
                  <div className=" text-end mb-3">
                    {!reorderLoading && (
                      <Button
                        variant="secondary"
                        onClick={handleUpdateDisplayOrder}
                        disabled={reorderLoading}
                        className="ms-5"
                      >
                        Update Display Order
                      </Button>
                    )}
                    {reorderLoading && (
                      <span className="ms-2 ">
                        <small className="text-primary fs-16">
                          <span
                            className="spinner-border spinner-border-sm me-1"
                            role="status"
                          ></span>
                          Updating order...
                        </small>
                      </span>
                    )}
                  </div>
                  {manageProductsGallery.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-muted">
                        No images found for this product
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="mb-3 d-flex justify-content-between align-items-center">
                        <small className="text-info">
                          🎯 Drag and drop images to reorder them
                        </small>
                        <small className="text-muted">
                          {manageProductsGallery.length} image(s) total
                        </small>
                      </div>

                      <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable
                          droppableId="gallery-items"
                          direction="horizontal"
                        >
                          {(provided, snapshot) => (
                            <div
                              className="row"
                              {...provided.droppableProps}
                              ref={provided.innerRef}
                              style={{
                                minHeight: "200px",
                                backgroundColor: snapshot.isDraggingOver
                                  ? "rgba(13, 110, 253, 0.05)"
                                  : "transparent",
                                borderRadius: "8px",
                                transition: "background-color 0.2s ease",
                                padding: "10px",
                              }}
                            >
                              {manageProductsGallery.map((item, index) => (
                                <Draggable
                                  key={item.id.toString()}
                                  draggableId={item.id.toString()}
                                  index={index}
                                  isDragDisabled={reorderLoading}
                                >
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      className="col-md-3 col-sm-6 mb-3"
                                      style={{
                                        ...provided.draggableProps.style,
                                        transform: snapshot.isDragging
                                          ? provided.draggableProps.style
                                              ?.transform
                                          : "none",
                                      }}
                                    >
                                      <div
                                        className={`card h-100 ${
                                          snapshot.isDragging
                                            ? "shadow-lg border-primary"
                                            : "shadow-sm"
                                        }`}
                                        style={{
                                          transition: snapshot.isDragging
                                            ? "none"
                                            : "all 0.2s ease",
                                          transform: snapshot.isDragging
                                            ? "rotate(5deg)"
                                            : "rotate(0deg)",
                                          opacity: snapshot.isDragging
                                            ? 0.9
                                            : 1,
                                        }}
                                      >
                                        <div className="position-relative">
                                          {/* Drag Handle */}
                                          <div
                                            {...provided.dragHandleProps}
                                            className="position-absolute bg-primary text-white d-flex align-items-center justify-content-center"
                                            style={{
                                              top: "8px",
                                              left: "8px",
                                              width: "32px",
                                              height: "32px",
                                              borderRadius: "6px",
                                              cursor: reorderLoading
                                                ? "not-allowed"
                                                : "grab",
                                              zIndex: 2,
                                              fontSize: "14px",
                                              fontWeight: "bold",
                                              boxShadow:
                                                "0 2px 4px rgba(0,0,0,0.2)",
                                            }}
                                            title="Drag to reorder"
                                          >
                                            {index + 1}
                                          </div>

                                          {/* Drag Handle Icon */}
                                          <div
                                            className="position-absolute text-white"
                                            style={{
                                              top: "45px",
                                              left: "12px",
                                              fontSize: "16px",
                                              zIndex: 2,
                                              textShadow:
                                                "1px 1px 2px rgba(0,0,0,0.7)",
                                              pointerEvents: "none",
                                            }}
                                          >
                                            ⋮⋮
                                          </div>

                                          <img
                                            src={item.imageURL}
                                            alt={`Product ${index + 1}`}
                                            className="card-img-top"
                                            style={{
                                              width: "100%",
                                              height: "200px",
                                              objectFit: "cover",
                                              userSelect: "none",
                                              borderRadius:
                                                "calc(0.375rem - 1px) calc(0.375rem - 1px) 0 0",
                                            }}
                                            onError={(e) => {
                                              e.target.src =
                                                allImages.ImagePlaceholder;
                                            }}
                                            onDragStart={(e) =>
                                              e.preventDefault()
                                            }
                                          />

                                          {/* Delete Button */}
                                          <button
                                            type="button"
                                            className="btn btn-danger btn-sm position-absolute d-flex align-items-center justify-content-center"
                                            style={{
                                              top: "8px",
                                              right: "8px",
                                              width: "28px",
                                              height: "28px",
                                              borderRadius: "50%",
                                              fontSize: "16px",
                                              lineHeight: "1",
                                              zIndex: 2,
                                            }}
                                            onClick={() =>
                                              handleDelete(item.id)
                                            }
                                            title="Delete Image"
                                            disabled={
                                              reorderLoading ||
                                              snapshot.isDragging
                                            }
                                          >
                                            ×
                                          </button>

                                          {/* Overlay for dragging state */}
                                          {snapshot.isDragging && (
                                            <div
                                              className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center"
                                              style={{
                                                top: 0,
                                                left: 0,
                                                backgroundColor:
                                                  "rgba(13, 110, 253, 0.1)",
                                                borderRadius:
                                                  "calc(0.375rem - 1px)",
                                                zIndex: 1,
                                              }}
                                            >
                                              <div className="text-primary fw-bold">
                                                Moving...
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>
                    </>
                  )}
                </>
              )}
            </div>
            <div className="col-lg-4 justify-content-center d-flex">
              <MultipleFileUpload
                onFilesChange={handleFilesChange}
                selectedFiles={selectedFiles}
              />
            </div>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer className="pt-3">
        <Button variant="danger" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="secondary"
          onClick={handleSubmit}
          disabled={
            uploadLoading || selectedFiles.length === 0 || reorderLoading
          }
        >
          {uploadLoading ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
              ></span>
              Uploading...
            </>
          ) : (
            "Upload Images"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ProductGalleryModal;
